/** Recognising a failed dynamic import.
 *
 * Every screen in this app is a `lazy()` chunk, and `infra/nginx/default.conf`
 * answers a request for a hashed asset it does not have with 404 — deliberately,
 * because the alternative was index.html wearing a `.js` name. The cost of that
 * correctness is this case: a tab left open across a frontend release asks for a
 * chunk hash that no longer exists, the import rejects, and the rejection goes
 * straight past `<Suspense>` — which catches *pending*, never *failed*.
 *
 * The remedy is a reload, and it is the only remedy, so the app has to be able
 * to tell this apart from a genuine bug. There is no error class to check: a
 * failed dynamic import surfaces as `TypeError` in Chrome and Safari, a plain
 * `Error` in Firefox, and Vite's preload helper rejects with wording of its own.
 * The message is all there is.
 *
 * Lives in `lib/` and not beside the component that uses it because this is the
 * part with logic in it, and the frontend suite runs in `environment: 'node'`
 * with no DOM — so this is the part that can be tested properly. See
 * chunk-error.test.ts.
 */

/** Patterns each engine uses for "the module could not be fetched".
 *
 * Deliberately broad. A false positive costs the user one unnecessary reload;
 * a false negative strands them on a dead screen with no way forward.
 */
const CHUNK_FAILURE = [
  // Chrome, Edge, Safari — and Vite's own preload helper re-throws this shape.
  /Failed to fetch dynamically imported module/i,
  // Firefox.
  /error loading dynamically imported module/i,
  // Safari's wording when the script itself 404s.
  /Importing a module script failed/i,
  // webpack-era wording, still produced by some toolchains and worth catching.
  /Loading chunk \S+ failed/i,
  /ChunkLoadError/i,
  // Vite, when a stylesheet referenced by modulepreload is gone.
  /Unable to preload CSS/i,
]

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false
  const text = `${error.name}: ${error.message}`
  return CHUNK_FAILURE.some((pattern) => pattern.test(text))
}
